/**
 * NextBriefWidget — countdown to the next automated daily brief.
 *
 * TURN CC · 29 — adds a live progress bar when a run is in flight.
 *
 * States :
 *  1. IDLE     — pipeline is dormant. Show countdown "In Xh Ymin · 05:00 SGT · <day>".
 *  2. RUNNING  — pipeline is in flight (either fired by cron, or manual
 *                POST /api/daily-brief-v2/full-run). Show:
 *                   • pulse dot + "Running now · <current step label>"
 *                   • horizontal progress bar (gradient blue → indigo)
 *                     driven by `progress_pct`.
 *                   • "Started Xmin ago · ETA ~Ymin"
 *                   • expandable "Show details" → per-step timeline.
 *  3. DONE     — for a 3s grace period after completion, keep the bar at
 *                100% (emerald if success, red if failed). Then revert to
 *                the idle countdown for the next cron slot.
 *
 * Source of truth for timing (all TZ-agnostic):
 *   - `/api/scheduler/status` → next_run_utc for `product1_auto_generation`.
 *   - `/api/daily-brief-v2/live-progress` → in-flight run snapshot.
 */
import { useEffect, useMemo, useState } from "react";
import {
  CalendarClock,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Circle,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { formatInTimeZone } from "date-fns-tz";
import { fr } from "date-fns/locale";

import { useSchedulerStatus, pickJob } from "@/hooks/useSchedulerStatus";
import { useLiveProgress } from "@/hooks/useLiveProgress";

const T = {
  navy: "#0F1B2D",
  mut: "#3D4B62",
  sub: "#5B6B82",
  border: "#E5E9F0",
  bg: "#F8FAFC",
  accent: "#0FA9DB",
  emerald: "#059669",
  red: "#DC2626",
};

const SGT_TZ = "Asia/Singapore";
// Product 1 cron: mon-fri 05:00 SGT (see backend/scheduler/scheduler.py).
const CRON_HOUR_SGT = 5;
const CRON_MINUTE_SGT = 0;
const JOB_ID = "product1_auto_generation";
// Grace window (ms) after a run finishes during which we keep the terminal
// state visible before flipping back to the idle countdown.
const TERMINAL_GRACE_MS = 15_000;

/* ----- helpers -------------------------------------------------------- */

function _fallbackNextSGT(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: SGT_TZ, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    weekday: "short", hour12: false,
  }).formatToParts(now);
  const p = Object.fromEntries(parts.map((x) => [x.type, x.value]));
  const y = Number(p.year), mo = Number(p.month), d = Number(p.day);
  const h = Number(p.hour), mi = Number(p.minute);
  let dayOffset = (h > CRON_HOUR_SGT
    || (h === CRON_HOUR_SGT && mi >= CRON_MINUTE_SGT)) ? 1 : 0;
  const nowUtcMs = Date.UTC(y, mo - 1, d, Number(p.hour), Number(p.minute),
                             Number(p.second));
  const utcParts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "UTC", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).formatToParts(now);
  const q = Object.fromEntries(utcParts.map((x) => [x.type, x.value]));
  const nowUtcRealMs = Date.UTC(+q.year, +q.month - 1, +q.day,
                                 +q.hour, +q.minute, +q.second);
  const offsetMs = nowUtcMs - nowUtcRealMs;
  for (let i = 0; i < 8; i += 1) {
    const candidateSgtMs = Date.UTC(y, mo - 1, d + dayOffset + i,
                                     CRON_HOUR_SGT, CRON_MINUTE_SGT, 0);
    const candidateUtcMs = candidateSgtMs - offsetMs;
    const candidate = new Date(candidateUtcMs);
    const wd = formatInTimeZone(candidate, SGT_TZ, "i");
    const wdNum = Number(wd);
    if (wdNum >= 1 && wdNum <= 5 && candidateUtcMs > now.getTime()) {
      return candidate;
    }
  }
  return new Date(now.getTime() + 24 * 3600_000);
}

function _fmtCountdown(ms) {
  const totalMin = Math.max(0, Math.floor(ms / 60_000));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}min`;
  return `${h}h ${m}min`;
}

function _fmtRelPast(iso) {
  if (!iso) return "never";
  const diffM = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (diffM < 1) return "just now";
  if (diffM < 60) return `${diffM}m ago`;
  if (diffM < 60 * 24) return `${Math.round(diffM / 60)}h ago`;
  return `${Math.round(diffM / 60 / 24)}d ago`;
}

function _fmtElapsedShort(iso) {
  if (!iso) return null;
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}min`;
}

function _fmtEta(seconds) {
  if (seconds == null || !Number.isFinite(seconds)) return null;
  if (seconds < 60) return `${seconds}s`;
  return `${Math.round(seconds / 60)}min`;
}

/* ----- sub-components ------------------------------------------------- */

function StepIcon({ status }) {
  if (status === "completed") {
    return <CheckCircle2 className="h-3.5 w-3.5" style={{ color: T.emerald }} />;
  }
  if (status === "running") {
    return <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: T.accent }} />;
  }
  if (status === "failed") {
    return <AlertCircle className="h-3.5 w-3.5" style={{ color: T.red }} />;
  }
  return <Circle className="h-3.5 w-3.5" style={{ color: "#94A3B8" }} strokeWidth={2} />;
}

function StepTimeline({ steps }) {
  return (
    <ul
      data-testid="next-brief-steps"
      className="mt-3 space-y-1.5 pl-1"
      style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
    >
      {steps.map((s) => (
        <li
          key={s.name}
          className="flex items-center gap-2"
          style={{ fontSize: 11, color: T.sub }}
        >
          <StepIcon status={s.status} />
          <span
            style={{
              flex: 1,
              color: s.status === "running" ? T.navy
                    : s.status === "completed" ? T.mut
                    : s.status === "failed" ? T.red
                    : "#94A3B8",
              fontWeight: s.status === "running" ? 600 : 400,
            }}
          >
            {s.label || s.name}
          </span>
          {s.status === "completed" && s.duration_s != null && (
            <span style={{ color: "#94A3B8", tabularNums: true }}>
              {s.duration_s < 1
                ? `${Math.round(s.duration_s * 1000)}ms`
                : `${s.duration_s.toFixed(1)}s`}
            </span>
          )}
          {s.status === "failed" && s.error && (
            <span style={{ color: T.red }} title={s.error}>
              failed
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

function ProgressBar({ pct, tone }) {
  const gradient = tone === "success"
    ? "linear-gradient(90deg, #10B981, #059669)"
    : tone === "fail"
      ? "linear-gradient(90deg, #F87171, #DC2626)"
      : "linear-gradient(90deg, #0FA9DB, #4F46E5)";
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div
      className="w-full rounded-full overflow-hidden"
      style={{ height: 6, background: "#E2E8F0" }}
      data-testid="next-brief-progress-bar"
    >
      <div
        style={{
          height: "100%",
          width: `${clamped}%`,
          background: gradient,
          borderRadius: 999,
          transition: "width 500ms cubic-bezier(0.22, 0.61, 0.36, 1)",
        }}
      />
    </div>
  );
}

/* ----- RunningState (running or just-terminal grace window) ---------- */

function RunningState({ snap }) {
  const [showSteps, setShowSteps] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  void tick;

  const isRunning = snap.status === "running";
  const isFailed = snap.status === "failed";
  const isDone = !isRunning && !isFailed;

  const tone = isFailed ? "fail" : isDone ? "success" : "info";
  const pct = isRunning ? snap.progress_pct : 100;

  const currentStep = useMemo(() => {
    if (!isRunning) return null;
    return (snap.steps || []).find((s) => s.status === "running") || null;
  }, [snap.steps, isRunning]);

  const statusLabel = isRunning
    ? currentStep?.label || "Preparing"
    : isFailed
      ? "Run failed"
      : "Run complete";

  const startedShort = _fmtElapsedShort(snap.started_at);
  const eta = _fmtEta(snap.eta_seconds);

  return (
    <div className="w-full">
      {/* Row 1: pulse dot + status text */}
      <div className="flex items-center gap-3 mb-3">
        <span
          aria-hidden
          style={{
            width: 8, height: 8, borderRadius: "50%",
            background: isFailed ? T.red : isDone ? T.emerald : T.accent,
            boxShadow: isRunning ? `0 0 0 0 ${T.accent}` : "none",
            animation: isRunning ? "hm-pulse-dot 1.6s ease-out infinite" : "none",
            flexShrink: 0,
          }}
        />
        <div
          data-testid="next-brief-running-label"
          style={{ color: T.navy, fontSize: 15, fontWeight: 600, lineHeight: 1.2 }}
        >
          {isRunning ? "Running now" : statusLabel}
          {isRunning && (
            <>
              <span style={{ color: T.border, margin: "0 8px", opacity: 0.4 }}>·</span>
              <span style={{ color: T.mut, fontWeight: 500, fontSize: 13 }}>
                {statusLabel}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Row 2: progress bar + % */}
      <div className="flex items-center gap-3 mb-2.5">
        <ProgressBar pct={pct} tone={tone} />
        <span
          data-testid="next-brief-progress-pct"
          style={{
            color: T.navy, fontSize: 12, fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
            minWidth: 32, textAlign: "right",
          }}
        >
          {Math.round(pct)}%
        </span>
      </div>

      {/* Row 3: timing */}
      <div
        className="flex items-center gap-1.5 flex-wrap"
        style={{
          color: T.sub, fontSize: 11,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        }}
      >
        {startedShort && (
          <>
            <span>Started {startedShort} ago</span>
          </>
        )}
        {isRunning && eta && (
          <>
            <span style={{ color: T.border, opacity: 0.5 }}>·</span>
            <span>ETA ~{eta}</span>
          </>
        )}
        {isDone && snap.brief_id && (
          <>
            <span style={{ color: T.border, opacity: 0.5 }}>·</span>
            <span style={{ color: T.emerald, fontWeight: 600 }}>
              brief {String(snap.brief_id).slice(0, 8)}
            </span>
          </>
        )}
      </div>

      {/* Row 4: expandable step timeline */}
      {Array.isArray(snap.steps) && snap.steps.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setShowSteps((v) => !v)}
            data-testid="next-brief-steps-toggle"
            className="mt-2 flex items-center gap-1.5 text-xs transition-opacity hover:opacity-70"
            style={{
              color: T.sub,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              background: "none", border: "none", padding: 0, cursor: "pointer",
            }}
          >
            {showSteps
              ? <ChevronUp className="h-3 w-3" />
              : <ChevronDown className="h-3 w-3" />}
            <span>{showSteps ? "Hide details" : "Show details"}</span>
          </button>
          {showSteps && <StepTimeline steps={snap.steps} />}
        </>
      )}
    </div>
  );
}

/* ----- IdleState (countdown) ----------------------------------------- */

function IdleState({ targetDate, nowMs }) {
  const remainMs = targetDate.getTime() - nowMs;
  const sgtHhmm = useMemo(
    () => formatInTimeZone(targetDate, SGT_TZ, "HH:mm"),
    [targetDate],
  );
  const sgtWeekday = useMemo(
    () => formatInTimeZone(targetDate, SGT_TZ, "EEEE d MMM", { locale: fr }),
    [targetDate],
  );
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <CalendarClock
        className="h-5 w-5" style={{ color: T.accent }} strokeWidth={2} />
      <div
        data-testid="next-brief-countdown"
        style={{ color: T.navy, fontSize: 18, fontWeight: 600 }}
      >
        In {_fmtCountdown(remainMs)}
      </div>
      <span style={{ color: T.border, opacity: 0.4 }}>·</span>
      <div
        data-testid="next-brief-time"
        className="flex items-baseline gap-1.5"
        style={{ color: T.mut, fontSize: 13 }}
      >
        <span style={{ fontWeight: 500 }}>{sgtHhmm}</span>
        <span
          style={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 11, letterSpacing: "0.06em",
            color: "#64748B", textTransform: "uppercase",
          }}
        >
          SGT
        </span>
      </div>
      <span style={{ color: T.border, opacity: 0.4 }}>·</span>
      <div
        data-testid="next-brief-day"
        style={{ color: T.mut, fontSize: 13, fontWeight: 500 }}
      >
        {sgtWeekday}
      </div>
    </div>
  );
}

/* ----- main component ------------------------------------------------ */

export default function NextBriefWidget({ lastBrief }) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const { data: schedStatus } = useSchedulerStatus();
  const { data: progressData } = useLiveProgress();

  const targetDate = useMemo(() => {
    const job = pickJob(schedStatus, JOB_ID);
    if (job?.next_run_utc) {
      const t = new Date(job.next_run_utc);
      if (!Number.isNaN(t.getTime())) return t;
    }
    return _fallbackNextSGT(new Date(nowMs));
  }, [schedStatus, nowMs]);

  // Decide which state to render.
  // - RUNNING  : progressData.active && status == 'running'
  // - JUST FINISHED : progressData.active && status terminal && finished < 15s ago
  // - IDLE     : otherwise
  const runningSnap = progressData?.active ? progressData : null;
  const showRunning = useMemo(() => {
    if (!runningSnap) return false;
    if (runningSnap.status === "running") return true;
    // Terminal — keep visible for TERMINAL_GRACE_MS after completed_at.
    if (runningSnap.completed_at) {
      const doneMs = new Date(runningSnap.completed_at).getTime();
      if (Number.isFinite(doneMs) && nowMs - doneMs < TERMINAL_GRACE_MS) {
        return true;
      }
    }
    return false;
  }, [runningSnap, nowMs]);

  const lastRunLine = useMemo(() => {
    if (!lastBrief) return null;
    const parts = [];
    parts.push(`Last run: ${_fmtRelPast(lastBrief.updated_at || lastBrief.created_at)}`);
    if (typeof lastBrief.article_count === "number") {
      parts.push(`${lastBrief.article_count} article${lastBrief.article_count === 1 ? "" : "s"}`);
    }
    if (lastBrief.status && lastBrief.status !== "draft") {
      parts.push(`status ${lastBrief.status}`);
    }
    return parts.join(" · ");
  }, [lastBrief]);

  return (
    <section
      data-testid="next-brief-widget"
      style={{
        background: T.bg, border: `1px solid ${T.border}`,
        borderRadius: 12, padding: "20px 24px",
        marginBottom: 16,
      }}
    >
      <div
        style={{
          color: T.sub, fontSize: 10, fontWeight: 700,
          letterSpacing: "0.14em", textTransform: "uppercase",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          marginBottom: 10,
        }}
      >
        Next automated brief
      </div>

      {showRunning
        ? <RunningState snap={runningSnap} />
        : <IdleState targetDate={targetDate} nowMs={nowMs} />}

      {!showRunning && lastRunLine && (
        <div
          style={{
            color: T.sub, fontSize: 12, marginTop: 10,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          }}
          data-testid="next-brief-last-run"
        >
          {lastRunLine}
        </div>
      )}

      <style>{`
        @keyframes hm-pulse-dot {
          0%   { box-shadow: 0 0 0 0    rgba(15,169,219,0.55); }
          70%  { box-shadow: 0 0 0 10px rgba(15,169,219,0);    }
          100% { box-shadow: 0 0 0 0    rgba(15,169,219,0);    }
        }
      `}</style>
    </section>
  );
}
